import { useState, useEffect } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Users, Baby, HeartHandshake } from "lucide-react";
import RevealText from "../components/RevealText";
import KitDownloadModal from "../components/KitDownloadModal";
import { BRAND } from "../lib/brand";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// modalConfig: se presente apre il modal con GDPR + Airtable sync invece del link esterno
const RESOURCES = [
  {
    id: 1,
    title: "La mia guida gratuita",
    excerpt: "Un piccolo dono per iniziare: contenuti pratici e sguardo rispettoso, coerente con il Metodo T.A.T.A.®. Il modulo distingue automaticamente il tuo profilo (genitore o tata).",
    category: "Introduzione",
    audience: "entrambi",
    link: "https://form.jotform.com/250741911322347",
    cta: "Scarica la guida",
  },
  {
    id: 2,
    title: "Guida gratuita sul Sonno",
    excerpt: "Una guida pratica dedicata al sonno del neonato e del bambino. Osservare, comprendere, accompagnare — senza formule rigide, con rispetto dei suoi tempi.",
    category: "Sonno",
    audience: "entrambi",
    link: "https://form.jotform.com/251585680077364",
    cta: "Scarica la guida",
  },
  {
    id: 3,
    title: "Filastrocche delle ditina",
    excerpt: "Un piccolo dono pensato per i genitori (routine prima della nanna, cura, igiene) e per le tate come strumento di relazione e divertimento durante il gioco.",
    category: "Gioco & Relazione",
    audience: "entrambi",
    cta: "Scarica il PDF",
    modalConfig: {
      kitName: "Filastrocche delle ditina",
      downloadFileName: "Filastrocche-delle-ditina-Teresa-Vilardo.pdf",
      downloadUrl: "https://customer-assets-wrfwihn1.emergentagent.net/job_brand-vilardo/artifacts/2kfgb66d_TV%20-%20DEF%20Filastrocche%20delle%20ditina%20%28Riviste%29%20%282%29.pdf",
      title: "Filastrocche",
      titleLine2: "delle ditina",
      subtitle: "Il tuo regalo",
      intro: "Lasciami i tuoi dati per ricevere il PDF. Ti manderò anche una copia via email 💌",
    },
  },
  {
    id: 4,
    title: "Giocare e crescere con la palla sensoriale",
    excerpt: "Un piccolo strumento per genitori e tate: come usare la palla sensoriale per stimolare i sensi, il movimento e la relazione col bambino. Idee semplici, quotidiane, da riprodurre in casa.",
    category: "Gioco & Sviluppo",
    audience: "entrambi",
    cta: "Scarica la guida",
    modalConfig: {
      kitName: "Giocare e crescere con la palla sensoriale",
      downloadFileName: "Palla-sensoriale-Teresa-Vilardo.pdf",
      downloadUrl: "https://customer-assets-wrfwihn1.emergentagent.net/job_brand-vilardo/artifacts/5ufloys4_Marrone%20e%20Giallo%20Moderno%20Guida%20al%20Carosello%20Virale%20Post%20Instagram%20%281%29.pdf",
      title: "Palla",
      titleLine2: "sensoriale",
      subtitle: "La tua risorsa",
      intro: "Lasciami i tuoi dati per ricevere la guida. Ti manderò anche una copia via email 💌",
    },
  },
];

const FILTERS = [
  { key: "tutti", label: "Tutti", icon: Users },
  { key: "genitori", label: "Per Genitori", icon: HeartHandshake },
  { key: "tate", label: "Per Tate", icon: Baby },
];

export default function Risorse() {
  const [filter, setFilter] = useState("tutti");
  const [activeModal, setActiveModal] = useState(null);
  const [dynamicResources, setDynamicResources] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  useEffect(() => {
    axios.get(`${API_BASE}/resources`).then((r) => setDynamicResources(r.data || [])).catch(() => {});
  }, []);

  // Merge legacy + dynamic. Dynamic ones use resourceId + modalConfig auto-generated.
  const dynamicAsCards = dynamicResources.map((d) => ({
    id: `db-${d.id}`,
    title: d.title,
    excerpt: d.description || d.subtitle || "",
    category: d.subtitle || "Biblioteca T.A.T.A.",
    audience: d.category,
    cta: d.cta_text || "Scarica gratuitamente",
    coverImage: d.cover_image_url || null,
    featured: d.featured,
    modalConfig: {
      resourceId: d.id,
      kitName: d.title,
      downloadFileName: `${d.title.replace(/[^a-zA-Z0-9]+/g, "-")}.pdf`,
      downloadUrl: d.pdf_url,
      title: d.title.split(" ").slice(0, 2).join(" "),
      titleLine2: d.title.split(" ").slice(2).join(" ") || d.subtitle || "",
      subtitle: "La tua risorsa",
      intro: "Compila il modulo per ricevere la tua risorsa 💌",
    },
  }));

  const allResources = [...dynamicAsCards, ...RESOURCES];

  const filtered = allResources.filter((r) => {
    if (filter === "tutti") return true;
    if (filter === "genitori") return r.audience === "genitori" || r.audience === "entrambi";
    if (filter === "tate") return r.audience === "tate" || r.audience === "entrambi";
    return true;
  });

  return (
    <div data-testid="risorse-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[70vh] pt-32 md:pt-40 pb-12 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Biblioteca T.A.T.A.</p>
          <h1 className="font-display text-[14vw] md:text-[11vw] lg:text-[14rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["Biblioteca", <span key="1" className="text-[#FF2768]">T.A.T.A.</span>]} stagger={0.14} />
          </h1>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-8 md:col-start-3 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic text-center">
              Le risorse gratuite di Teresa Vilardo — per <strong>genitori</strong>, <strong>tate</strong> ed <strong>educatori</strong>.
            </p>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-52 w-32 md:w-52 rounded-full opacity-80"
        />
      </section>

      {/* FILTERS */}
      <section className="px-6 md:px-10 py-8 border-y border-[#81C3C9]/40 bg-white sticky top-[80px] z-20 backdrop-blur-md" data-testid="filters-section">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center justify-center gap-2 md:gap-3">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`inline-flex items-center gap-2 px-5 md:px-6 py-2.5 rounded-full text-sm font-semibold uppercase tracking-wider transition-all ${
                  active
                    ? "bg-[#FF2768] text-white shadow-lg"
                    : "bg-transparent text-[#3D2140] border border-[#3D2140]/20 hover:border-[#FF2768] hover:text-[#FF2768]"
                }`}
              >
                <Icon size={14} /> {f.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* RESOURCES GRID */}
      <section className="px-6 md:px-10 py-16 md:py-24 max-w-[1600px] mx-auto" data-testid="resources-grid">
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {filtered.length === 0 ? (
              <div className="md:col-span-3 text-center py-24 text-[#3D2140]/60">
                <p className="font-editorial text-2xl italic">Presto arriveranno nuove risorse per questa categoria.</p>
              </div>
            ) : (
              filtered.map((r, idx) => (
                <motion.article
                  key={r.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.06 }}
                  className="group relative p-8 md:p-10 rounded-[32px] bg-white border border-[#81C3C9]/40 hover:border-[#FF2768] transition-colors duration-500 flex flex-col"
                  data-testid={`resource-card-${r.id}`}
                >
                  <div className="flex items-center gap-2 mb-6">
                    <AudienceBadge audience={r.audience} />
                    <span className="text-[10px] uppercase tracking-widest text-[#81C3C9] font-semibold">· {r.category}</span>
                  </div>

                  <div className="flex items-start gap-4 mb-6">
                    <span className="h-14 w-14 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center shrink-0">
                      <Download size={22} />
                    </span>
                    <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] leading-[0.95] group-hover:text-[#FF2768] transition-colors">
                      {r.title}
                    </h3>
                  </div>

                  <p className="text-[#3D2140]/75 leading-relaxed mb-8 flex-1">{r.excerpt}</p>

                  {r.modalConfig ? (
                    <button
                      type="button"
                      onClick={() => setActiveModal(r.modalConfig)}
                      data-testid={`resource-download-${r.id}`}
                      className="inline-flex items-center gap-2 text-[#FF2768] font-semibold uppercase tracking-wider text-sm link-underline text-left"
                    >
                      {r.cta || "Scarica"} <ArrowRight size={14} />
                    </button>
                  ) : (
                    <a
                      href={r.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`resource-download-${r.id}`}
                      className="inline-flex items-center gap-2 text-[#FF2768] font-semibold uppercase tracking-wider text-sm link-underline"
                    >
                      {r.cta || "Scarica"} <ArrowRight size={14} />
                    </a>
                  )}
                </motion.article>
              ))
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      {/* CTA CHIUSURA */}
      <section className="px-6 md:px-10 py-24 bg-[#81C3C9]/25" data-testid="risorse-cta-section">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="font-editorial text-[#FF2768] text-xl mb-4">— Non trovi quello che cerchi?</p>
          <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9] mb-6">
            Scrivimi per una<br/><span className="text-[#FF2768]">consulenza personalizzata.</span>
          </h2>
          <p className="text-[#3D2140]/75 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Ogni situazione è unica. Se hai una domanda specifica o un percorso da costruire insieme, ci sono.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/prenota" className="btn-tv-primary" data-testid="risorse-cta-booking">
              Prenota una consulenza <ArrowRight size={16} />
            </Link>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-tv-outline" data-testid="risorse-cta-whatsapp">
              Scrivimi su WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Kit download modal (GDPR + Airtable sync) */}
      <KitDownloadModal
        open={!!activeModal}
        onClose={() => setActiveModal(null)}
        {...(activeModal || {})}
      />
    </div>
  );
}

function AudienceBadge({ audience }) {
  const map = {
    genitori: { label: "Genitori", bg: "bg-[#FF2768]/10", text: "text-[#FF2768]" },
    tate: { label: "Tate", bg: "bg-[#81C3C9]/25", text: "text-[#4E9EA5]" },
    entrambi: { label: "Genitori & Tate", bg: "bg-[#FFE45C]/30", text: "text-[#3D2140]" },
  };
  const b = map[audience] || map.entrambi;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${b.bg} ${b.text}`}>
      {b.label}
    </span>
  );
}
